/**
 * One reading of an Axios error.
 *
 * Every planner surface used to reach into `err.response.data.message` on
 * its own, with its own fallback and its own idea of what a string body
 * means. The shape is read here once; callers decide only the fallback.
 */
export interface ApiErrorDetails {
	status?: number;
	message: string;
	/** The body's machine-readable reason (a 409's `code`), when the server sent one. */
	code?: string | null;
}

interface AxiosLikeError {
	response?: {
		status?: number;
		data?: { message?: string; code?: string | null } | string;
	};
}

/** Status, message and code of a failed request; `fallback` when the server said nothing. */
export const apiErrorDetails = (err: unknown, fallback = "The request failed."): ApiErrorDetails => {
	const response = (err as AxiosLikeError | undefined)?.response;
	const data = response?.data;
	const message =
		(typeof data === "string" ? data : data?.message) ||
		(err instanceof Error ? err.message : "") ||
		fallback;
	const code = typeof data === "object" && data ? data.code ?? null : null;
	return { status: response?.status, message, code };
};

/** The message the server sent, else the caller's fallback. */
export const apiErrorMessage = (err: unknown, fallback = "The request failed."): string => {
	const data = (err as AxiosLikeError | undefined)?.response?.data;
	const message = typeof data === "string" ? data : data?.message;
	return message || fallback;
};
